import type { ReportColumn } from '../models/column.types'
import type { FilterDropdownables, FilterDropdownableValue } from '../models/filter.types'
import type { ReportRow } from '../models/report.types'

import { isReportFilterDropdownableColumn } from './reportColumnKind'

export interface DropdownablesFromRowsOptions {
  /** Omit `values` above this cardinality while retaining an exact `unique_count`. */
  maxValues?: number
}

function dropdownableValue(value: unknown): value is FilterDropdownableValue {
  return typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value))
}

function valuesFromCell(value: unknown): FilterDropdownableValue[] {
  if (Array.isArray(value)) return value.filter(dropdownableValue)
  return dropdownableValue(value) ? [value] : []
}

function valueLimit(options: DropdownablesFromRowsOptions): number {
  const requested = options.maxValues
  if (requested === undefined) return Number.POSITIVE_INFINITY
  if (!Number.isFinite(requested)) return Number.POSITIVE_INFINITY
  return Math.max(0, Math.trunc(requested))
}

/** Derive stable first-seen distinct values without mutating controller-owned rows or columns. */
export function dropdownablesFromRows(
  rows: readonly ReportRow[],
  columns: readonly ReportColumn[],
  options: DropdownablesFromRowsOptions = {},
): FilterDropdownables {
  const limit = valueLimit(options)
  const result: Record<string, { unique_count: number; values?: FilterDropdownableValue[] }> = {}
  const seenFields = new Set<string>()

  for (const column of columns) {
    if (seenFields.has(column.field) || !isReportFilterDropdownableColumn(column)) continue
    seenFields.add(column.field)

    const values: FilterDropdownableValue[] = []
    const seenValues = new Set<FilterDropdownableValue>()
    for (const row of rows) {
      for (const value of valuesFromCell(row[column.field])) {
        if (seenValues.has(value)) continue
        seenValues.add(value)
        values.push(value)
      }
    }

    result[column.field] = {
      unique_count: values.length,
      ...(values.length <= limit ? { values } : {}),
    }
  }

  return result
}
